import { useEffect, useMemo, useState } from "react";

import { api } from "../../api/client";
import { AdminPageIntro } from "../../components/admin/AdminPageIntro";
import { UserActionModal } from "../../components/admin/UserActionModal";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { ResponsiveTable } from "../../components/ui/ResponsiveTable";
import { apiError } from "../../lib/apiError";
import { formatDateTime } from "../../lib/format";

type PlatformUser = {
  id: number;
  email: string;
  username: string;
  email_verified: boolean;
  is_active: boolean;
  is_test_user: boolean;
  is_platform_admin: boolean;
  mfa_enabled: boolean;
  deletion_requested_at: string | null;
  created_at: string;
  last_activity: string | null;
};

type ActionType = "activate" | "deactivate" | "revoke-sessions" | "cancel-deletion";

export function AdminUsersPage() {
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [pendingAction, setPendingAction] = useState<{
    user: PlatformUser;
    action: ActionType;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    const result = await api.GET("/api/platform/users");
    if (result.error) {
      setError(apiError(result.error, "Nie udało się wczytać użytkowników."));
      return;
    }
    const data = result.data as { users: PlatformUser[] };
    setUsers(data.users);
  }

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return users;
    }
    return users.filter(
      (user) =>
        user.email.toLowerCase().includes(q) ||
        user.username.toLowerCase().includes(q) ||
        String(user.id).includes(q),
    );
  }, [users, query]);

  async function confirmAction() {
    if (!pendingAction) {
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);

    const userId = pendingAction.user.id;
    let result;
    switch (pendingAction.action) {
      case "activate":
        result = await api.POST("/api/platform/users/activate", { body: { user_id: userId } });
        break;
      case "deactivate":
        result = await api.POST("/api/platform/users/deactivate", { body: { user_id: userId } });
        break;
      case "revoke-sessions":
        result = await api.POST("/api/platform/users/revoke-sessions", {
          body: { user_id: userId },
        });
        break;
      case "cancel-deletion":
        result = await api.POST("/api/platform/users/cancel-deletion", {
          body: { user_id: userId },
        });
        break;
    }

    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się wykonać akcji."));
      return;
    }

    const data = result.data as { message?: string };
    setSuccess(data.message ?? "Akcja wykonana.");
    setPendingAction(null);
    await load();
  }

  return (
    <div className="page__content page__content--admin">
      <AdminPageIntro>Metadane kont — bez danych gospodarstw.</AdminPageIntro>

      {error ? (
        <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      ) : null}
      {success ? (
        <AlertBanner variant="ok" message={success} onDismiss={() => setSuccess(null)} />
      ) : null}

      <div className="toolbar">
        <input
          className="input"
          type="search"
          placeholder="Szukaj po e-mail, nazwie lub ID…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      <div className="tbl-wrap">
        <ResponsiveTable className="tbl">
          <thead>
            <tr>
              <th>ID</th>
              <th>E-mail</th>
              <th>Nazwa</th>
              <th>Status</th>
              <th>MFA</th>
              <th>Ostatnia aktywność</th>
              <th>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((user) => (
              <tr key={user.id}>
                <td>{user.id}</td>
                <td>{user.email}</td>
                <td>{user.username}</td>
                <td>
                  {user.is_active ? "Aktywny" : "Nieaktywny"}
                  {user.deletion_requested_at ? " · usunięcie zaplanowane" : ""}
                  {user.is_platform_admin ? " · admin" : ""}
                </td>
                <td>{user.mfa_enabled ? "Tak" : "Nie"}</td>
                <td>{formatDateTime(user.last_activity)}</td>
                <td>
                  <div className="actions">
                    {!user.is_active ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setPendingAction({ user, action: "activate" })}
                      >
                        Aktywuj
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setPendingAction({ user, action: "deactivate" })}
                      >
                        Dezaktywuj
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setPendingAction({ user, action: "revoke-sessions" })}
                    >
                      Wyloguj
                    </button>
                    {user.deletion_requested_at ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setPendingAction({ user, action: "cancel-deletion" })}
                      >
                        Anuluj usunięcie
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      </div>

      <UserActionModal
        open={!!pendingAction}
        action={pendingAction?.action ?? null}
        email={pendingAction?.user.email ?? ""}
        busy={busy}
        onClose={() => setPendingAction(null)}
        onConfirm={() => void confirmAction()}
      />
    </div>
  );
}
